<template lang="pug">
.app-select-wrapper
  .select2-selection-counter.d-flex.align-items-center(
    v-if="showSelectionCount"
    @click.stop="toggleSummary"
  )
    | {{ summary }}
    span.select2-selection__clear(@click.stop="clearAll") &times;

  span.loading-container.form-control(
    v-if="isLoading"
    :style="{ width: select2Options.width }"
  )
    .spinner-wrapper.d-flex.mr-5
      span.hi.hi-spinner.hi-12.text-blue
    | Loading...

  .app-select(
    v-tooltip="hasTooltip"
    :class="cssClasses"
    :data-original-title="tooltipContent"
  )
    select.select2-hidden-accessible(
      :id="select2Id"
      :disabled="disabled"
    )
      slot
</template>

<script>
import { isEqual, isString } from 'lodash-es';
import VModelCompat from '~/mixins/v_model_compat';

const HIDE_SEARCH_DROPDOWN_CONTAINER_CLASS = 'has-hidden-search-dropdown';
const HIDE_SEARCH_DROPDOWN_CLASS = 'hide-search-dropdown';
const TOGGLE_WITH_SELECTION_COUNTER_CLASS = 'hidden';
const addTooltip = ($element, title, options = {}) => {
  const defaults = { title, container: 'body', trigger: 'hover' };

  $element.attr('title', title);
  $element.tooltip({ ...defaults, ...options });
};

export default {
  name: 'AppSelect',
  mixins: [VModelCompat],
  inject: {
    selectDropdownParent: { default: null }
  },
  props: {
    modelValue: {},
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    select2Options: {
      type: Object,
      required: false,
      default() {
        return {
          allowClear: false,
          multiple: false,
          width: '100%',
        };
      },
    },
    preventClearIds: {
      type: Array,
      default() {
        return [];
      },
    },
    select2Id: {
      type: String,
    },
    dropdownTestId: {
      type: String,
      default: null,
    },
    tooltipTitle: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledTooltip: {
      type: String,
    },
    async: {
      type: Boolean,
      default: true,
    },
    asyncDelay: {
      type: Number,
      default: 300,
    },
    hideDropdown: {
      type: Boolean,
      default: false,
    },
    maxVisibleOptions: {
      type: Number,
      required: false,
      default: 5,
    },
    label: {
      type: String,
      required: false,
      default: 'items',
    },
    // In compact mode, selected options are not added to the main panel.
    // This is ideal for large lists where the component would become very big
    // and move the rest of the UI down.
    compact: {
      type: Boolean,
      default: false,
    },
    // Always emit an @input event even if the same value
    alwaysEmit: {
      type: Boolean,
      default: false,
    },
    showLoadingState: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:modelValue', 'change', 'open', 'close', 'search', 'opening'],
  data() {
    return {
      selectionCount: 0,
      editing: false,
      mounted: false,
      destroyed: false,
      remountScheduled: false,
    };
  },
  computed: {
    showSelectionCount() {
      if (!this.editing && this.canShowCompactSummary) {
        return true;
      }

      return (
        this.select2Options.multiple &&
        !this.editing &&
        this.maxVisibleOptions > 0 &&
        this.selectionCount > this.maxVisibleOptions
      );
    },
    searchInputElement() {
      return this.selectElement?.data('select2').selection?.$search;
    },
    canShowCompactSummary() {
      return this.compact && this.selectionCount > 1;
    },
    summary() {
      return `${this.selectionCount} ${this.label.toLowerCase()}`;
    },
    cssClasses() {
      const classes = [];

      if (this.hideDropdown) {
        classes.push(HIDE_SEARCH_DROPDOWN_CONTAINER_CLASS);
      }

      if (this.showSelectionCount) {
        classes.push(TOGGLE_WITH_SELECTION_COUNTER_CLASS);
      }

      if (this.canShowCompactSummary) {
        classes.push('hide-selected-items');
      }

      return classes;
    },
    tooltipContent() {
      return this.disabled && isString(this.disabledTooltip) ? this.disabledTooltip : this.tooltipTitle || '';
    },
    hasTooltip() {
      return this.tooltipContent.length > 0;
    },
    fullSelect2Options() {
      const optionOverrides = {};

      if (this.options.length > 0) {
        optionOverrides.data = this.options;
      }

      if (this.hideDropdown) {
        optionOverrides.dropdownCssClass = HIDE_SEARCH_DROPDOWN_CLASS;
      }

      if (this.select2Options.multiple) {
        optionOverrides.closeOnSelect = false;
      }

      optionOverrides.escapeMarkup = (html) => {
        return html;
      };

      if (!this.select2Options.templateResult) {
        optionOverrides.templateResult = this.templateResult;
      }

      if (!this.select2Options.templateSelection) {
        optionOverrides.templateSelection = this.templateSelection;
      }

      if (this.selectDropdownParent) {
        optionOverrides.dropdownParent = this.selectDropdownParent;
      }

      return { ...this.select2Options, ...optionOverrides };
    },
    isLoading() {
      return this.showLoadingState && !this.mounted;
    },
  },
  watch: {
    modelCompatValue(value) {
      this.triggerChange(value);
    },
    select2Options(options, oldOptions) {
      if (!isEqual(options, oldOptions)) {
        this.scheduleRemount();
      }
    },
    options(options, oldOptions) {
      if (!this.select2Options.tags && !isEqual(options, oldOptions)) {
        this.scheduleRemount();
      }
    },
  },
  async mounted() {
    this.selectElement = $(this.$el).find('select');
    if (this.async) {
      // FIXME: This hack is so the Select2 component/library can pick up the changes
      // which sucks because now we're basically delaying all the mounting by 300 ms.
      // Bernat Jufré 2022-08-15
      await new Promise((resolve) => setTimeout(resolve, this.asyncDelay));

      if (this.hasInitialized()) {
        // We remount here because the options watcher may have mounted us in the meantime
        this.remountSelect();
      } else {
        this.mountSelect();
      }
      // fixes the missing summary with the selected items
      this.evaluateSelections();

      return;
    }

    this.mountSelect();
  },
  beforeUnmount() {
    this.destroyed = true;
  },
  unmounted() {
    const $select = $(this.$el).find('select');

    if ($select.data('select2')) {
      $select.off().select2('destroy');
    }
  },
  methods: {
    hasInitialized() {
      return !this.selectElement.hasClass('select2-hidden-accessible');
    },
    ignoreLegacyInit() {
      // The legacy Sprockets bundle (forms.js) runs `setupForms()` on DOM-ready and on every
      // AjaxDialog open, scanning the WHOLE document with `$('.select2:not([data-select2-ignore])')`
      // and calling `.select2()` on each match. select2 v4 puts a bare `select2` class on its own
      // container <span>, so that scan re-initializes select2 ON our container — hiding it and
      // spawning a broken duplicate. Opt out via the convention forms.js already honors (the same
      // marker AppMembersSelector / AppLazySelect use), so the legacy scan leaves our select alone.
      this.selectElement?.data?.('select2')?.$container?.attr?.('data-select2-ignore', '');
    },
    mountSelect() {
      // Guard against async callbacks firing after component destruction
      if (this.destroyed) {return;}

      this.selectElement.select2(this.fullSelect2Options).val(this.modelCompatValue).trigger('change.select2');
      this.ignoreLegacyInit();

      this.setupListeners();
      this.setupTooltipListener();
      this.stopPropagation();
      this.evaluateSelections();
      this.mounted = true;
    },
    // select2 teardown/re-init mutates DOM the renderer may be patching right now (watchers run
    // pre-flush, mid-patch). Defer to nextTick so the current patch completes first — mutating
    // mid-patch leaves half-mounted vnodes behind and every later update crashes with
    // "Cannot set properties of null (setting '__vnode')". Bursts coalesce into one remount.
    scheduleRemount() {
      // Before the initial mount there is nothing to remount — mounted() initializes select2
      // with the latest options anyway, and remounting mid-delay raced it (orphaned containers).
      if (!this.mounted || this.remountScheduled) {return;}

      this.remountScheduled = true;
      this.$nextTick(() => {
        this.remountScheduled = false;
        this.remountSelect();
      });
    },
    remountSelect() {
      // Guard against async callbacks firing after component destruction
      if (this.destroyed) {return;}

      this.selectionCount = this.compact && this.modelCompatValue ? this.modelCompatValue.length : 0;
      this.removeChangeListener();

      // Remounting a select with ajax and multiple options will cause the select to be empty
      // since the options are not available, so we skip it
      if (!(this.fullSelect2Options.ajax && this.fullSelect2Options.multiple)) {
        // Re-configuring a stateful jQuery plugin means tearing down the old instance first;
        // calling .select2() again without this would append a second, orphaned container.
        if (this.selectElement.data('select2')) {
          this.selectElement.select2('destroy');
        }
        this.selectElement.empty().select2(this.fullSelect2Options).val(this.modelCompatValue).trigger('change.select2');
        this.ignoreLegacyInit();
      }

      this.stopPropagation();
      this.setupListeners();
    },
    triggerChange(value) {
      if (!this.selectElement) {return;}

      $(this.$el).find('select').val(value).trigger('change.select2');

      if ($.data(this.selectElement[0], 'select2')) {
        this.countSelections();
      }
    },
    toggleSummary() {
      if (this.compact) {
        this.editing = true;
      }

      this.remountSelect();
      this.searchInputElement?.focus();
    },
    clearAll() {
      if (this.preventClearIds.length > 0) {
        const currentSelection = Array.isArray(this.modelCompatValue) ? this.modelCompatValue : [];
        const protectedIds = currentSelection.filter(id => this.preventClearIds.includes(id));

        if (protectedIds.length > 0) {
          this.emitModelCompatValue(protectedIds);
          this.$nextTick(() => this.triggerChange(protectedIds));
          if (this.compact) {this.updateCompactSummary();}

          return;
        }
      }

      this.emitModelCompatValue([]);
      if (this.compact) {
        this.updateCompactSummary();
      }
    },
    setupListeners() {
      this.selectElement
        .on('change.appSelect', () => {
          this.onChange();
          if (this.compact) {
            this.updateCompactSummary();
          }
        })
        .on('select2:select', (e) => this.onSelect(e))
        .on('select2:unselecting', (e) => this.onUnselecting(e))
        .on('select2:unselect', (e) => this.onUnselecting(e))
        .on('select2:close', () => this.evaluateSelections())
        .on('select2:clear', (e) => this.onClear(e))
        .on('select2:opening', (e) => {
          this.$emit('opening', e);
          if (this.compact) {
            this.updateCompactSummary();
          }
        });
    },
    updateCompactSummary() {
      setTimeout(() => {
        // wait for options to be updated
        const searchPanel = this.selectElement.next().find('.select2-search--inline');

        searchPanel.find('.c-summary').remove();
        if (this.canShowCompactSummary) {
          searchPanel.prepend(`<strong class="c-summary ml-5">${this.summary}</strong>`);
        }
      }, 0.01);
    },
    stopPropagation() {
      this.selectElement.on('select2:open', () => {
        const $dropdown = $('.select2-dropdown');

        $dropdown.click((e) => e.stopPropagation());

        if (this.dropdownTestId) {
          $dropdown.attr('data-testid', this.dropdownTestId);
        }
      });
    },
    removeChangeListener() {
      this.selectElement.off('change.appSelect');
    },
    setupTooltipListener() {
      $(this.$el).on('click', this.tooltipInitializer);
    },
    tooltipInitializer() {
      window.initTooltip($('.select2-container'));
    },
    removeTooltipListener() {
      $(this.$el).off('click', this.tooltipInitializer);
    },
    evaluateSelections() {
      this.editing = false;
      this.countSelections();
    },
    countSelections() {
      if (this.select2Options.multiple) {
        this.selectionCount = this.selectedData().length;
      }
    },
    onSelect(e) {
      if (e.params?.data?.disabled) {
        e.preventDefault();

        return;
      }
      if (this.select2Options.multiple) {
        this.searchInputElement?.focus(); // fixes the awkward scroll movement after an option was selected
      }
    },
    onUnselecting(e) {
      const optId = e.params?.data?.id;

      if (this.preventClearIds.includes(optId)) {
        e.preventDefault();

        return;
      }
      e.params?.args?.originalEvent?.stopPropagation();
      this.searchInputElement?.focus();
    },
    onClear(e) {
      if (this.preventClearIds.length > 0) {
        const currentSelection = Array.isArray(this.modelCompatValue) ? this.modelCompatValue : [];
        const protectedIds = currentSelection.filter(id => this.preventClearIds.includes(id));

        if (protectedIds.length > 0) {
          e.preventDefault();
          this.emitModelCompatValue(protectedIds);
          this.$nextTick(() => this.triggerChange(protectedIds));
        }
      }
    },
    onChange() {
      const selectedData = this.selectedData();
      const validSelectedData = selectedData.filter(obj => !obj.disabled);
      const selectedOptions = validSelectedData.map((obj) => obj.id);
      const payload = this.select2Options && this.select2Options.multiple ? selectedOptions : selectedOptions[0];

      this.editing = true;

      if (this.alwaysEmit || payload !== this.modelCompatValue) {
        this.emitModelCompatValue(payload);
      }
    },
    selectedData() {
      if (!this.selectElement || !this.selectElement.length || !$.data(this.selectElement[0], 'select2')) {
        return [];
      }

      return this.selectElement.select2('data');
    },
    ensureSelected(entry) {
      if (this.selectElement.find(`option[value="${entry.id}"]`).length === 0) {
        $('<option/>').attr('value', entry.id).text(entry.text).appendTo(this.selectElement);
        this.selectElement.val(this.modelCompatValue).trigger('change.select2');
      }
    },
    templateSelection(option, $container) {
      if (this.preventClearIds.includes(option.id)) {
        $container.find('.select2-selection__choice__remove').remove();
      }
      if (option.type) {
        $container.addClass(`selection-${option.type}`);
      }

      if (option.hint) {
        addTooltip($container, option.hint);
      }

      const elem = document.createElement('span');

      if (option.avatar) {
        elem.classList.add('align-items-center', 'd-flex');
        const avatar = document.createElement('img');

        avatar.classList.add('avatar', 'avatar-speck');
        avatar.src = option.avatar.speck ? option.avatar.speck : option.avatar;
        elem.appendChild(avatar);
      }

      const textNode = document.createTextNode(option.selectionText || option.text);

      elem.appendChild(textNode);

      return elem;
    },
    templateResult(option, container) {
      if (option.testId && container instanceof HTMLElement) {
        container.dataset.testid = option.testId;
      }

      if (option.new) {
        const elem = document.createElement('div');

        elem.classList.add('dropdown-new-item');
        elem.appendChild(document.createTextNode(option.text));

        return elem;
      }

      let elem = document.createElement('span');

      if (option.avatar) {
        elem.classList.add('align-items-center', 'd-flex');
        const avatar = document.createElement('img');

        avatar.classList.add('avatar', 'avatar-speck');
        avatar.src = option.avatar.speck ? option.avatar.speck : option.avatar;
        elem.appendChild(avatar);
      }

      if (option.error) {
        const child = document.createElement('i');

        child.classList.add('fa', 'fa-warning', 'text-danger');
        elem.appendChild(child);
        elem.appendChild(document.createTextNode(' '));
      }

      elem.appendChild(document.createTextNode(option.text));

      if (option.description) {
        const textElem = elem;
        const descriptionElem = document.createElement('sub');

        descriptionElem.classList.add('description');
        descriptionElem.appendChild(document.createTextNode(option.description));

        elem = document.createElement('span');
        elem.appendChild(textElem);
        elem.appendChild(descriptionElem);
      }

      if (typeof option.resultClass === 'string') {
        const classes = option.resultClass.split(/\s+/i);

        container.classList.add(...classes);
      }

      // NOTE: Because of technical limitations around select2, tooltips
      // should only be assigned to disabled items in the result list or
      // in a header option.
      if (
        (option.disabled && option.disabledHint) ||
        // This allows us to add tooltips to group headers in the select
        (option.hint && option.children)
      ) {
        const $container = $(container);
        const tooltip = option.disabledHint || option.hint;

        addTooltip($container, tooltip, option.tooltipOptions || {});
      }

      return elem;
    },

    // Public method that allows to select all options at once
    // Sample: <a @click="() => { $refs.countriesDropdown.selectAll() }"> Select all </a>
    selectAll() {
      this.selectElement.children().prop('selected', 'selected');
      this.selectElement.trigger('change');
      this.selectElement.trigger('select2:close');
    },
  },
};
</script>
